//AnimatesRoutes.js
import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import HomePage from "./pages/HomePage";
const NotFound = lazy(() => import("./pages/NotFoundPage"));
const AboutYoruPage = lazy(() => import("./pages/AboutYoruPage"));
const ContactYoruPage = lazy(() => import("./pages/ContactYoruPage"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ArticlesPage = lazy(() => import("./pages/ArticlesPage"));

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  out: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.4,
      ease: "easeInOut",
    },
  },
};

const fadeVariants = {
  initial: {
    opacity: 0,
  },
  in: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeInOut",
    },
  },
  out: {
    opacity: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const scaleVariants = {
  initial: {
    opacity: 0,
    scale: 0.95,
  },
  in: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: "backOut",
    },
  },
  out: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.4,
    },
  },
};

const getAnimationVariants = (pathname) => {
  switch (pathname) {
    case "/":
      return pageVariants;
    case "/about-yoru":
    case "/portfolio":
      return fadeVariants;
    case "/tarifs":
    case "/ContactYoru":
    case "/pricing":
      return scaleVariants;
    default:
      return fadeVariants;
  }
};

const AnimatedRoutes = ({ lang, setLang }) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Scroll every scrollable element to top on route change
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollTimer = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      document.querySelectorAll("*").forEach((el) => {
        if (el.scrollTop > 0) el.scrollTop = 0;
      });
    }, 50);

    return () => clearTimeout(scrollTimer);
  }, [pathname]);

  return (
    <Suspense fallback={null}>
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Home */}
        <Route
          path="/"
          element={
            <motion.div
              key="home"
              initial="initial"
              animate="in"
              exit="out"
              variants={getAnimationVariants(pathname)}
              className="page-container"
            >
              <HomePage
                lang={lang}
                setLang={setLang}
                onLanguageToggle={() => setLang(lang === "EN" ? "FR" : "EN")}
              />
            </motion.div>
          }
        />

        {/* New AboutYoru Page */}
        <Route
          path="/about-yoru"
          element={
            <motion.div
              key="about-yoru"
              initial="initial"
              animate="in"
              exit="out"
              variants={getAnimationVariants(pathname)}
              className="page-container"
            >
              <AboutYoruPage lang={lang} setLang={setLang} />
            </motion.div>
          }
        />

        {/* Contact Yoru Page */}
        <Route
          path="/ContactYoru"
          element={
            <motion.div
              key="Contact"
              initial="initial"
              animate="in"
              exit="out"
              variants={getAnimationVariants(pathname)}
              className="page-container"
            >
              <ContactYoruPage lang={lang} setLang={setLang} />
            </motion.div>
          }
        />

        {/* Portfolio Page */}
        <Route
          path="/portfolio"
          element={
            <motion.div
              key="portfolio"
              initial="initial"
              animate="in"
              exit="out"
              variants={getAnimationVariants(pathname)}
              className="page-container"
            >
              <Portfolio lang={lang} />
            </motion.div>
          }
        />

        {/* Pricing Page - French route */}
        <Route
          path="/tarifs"
          element={
            <motion.div
              key="pricing-fr"
              initial="initial"
              animate="in"
              exit="out"
              variants={getAnimationVariants(pathname)}
              className="page-container"
              style={{
                width: "100%",
                minHeight: "100vh",
              }}
            >
              <PricingPage lang={lang} setLang={setLang} />
            </motion.div>
          }
        />

        {/* Pricing Page - English route */}
        <Route
          path="/pricing"
          element={
            <motion.div
              key="pricing-en"
              initial="initial"
              animate="in"
              exit="out"
              variants={getAnimationVariants(pathname)}
              className="page-container"
              style={{
                width: "100%",
                minHeight: "100vh",
              }}
            >
              <PricingPage lang={lang} setLang={setLang} />
            </motion.div>
          }
        />

        {/* Articles Page */}
        <Route
          path="/articles"
          element={
            <motion.div
              key="articles"
              initial="initial"
              animate="in"
              exit="out"
              variants={fadeVariants}
              className="page-container"
            >
              <ArticlesPage lang={lang} setLang={setLang} />
            </motion.div>
          }
        />

        {/* Terms & Conditions Page */}
        <Route
          path="/terms"
          element={
            <motion.div
              key="terms"
              initial="initial"
              animate="in"
              exit="out"
              variants={fadeVariants}
              className="page-container"
            >
              <TermsPage lang={lang} setLang={setLang} />
            </motion.div>
          }
        />

        {/* Not Found Page */}
        <Route
          path="/not-found"
          element={
            <motion.div
              key="not-found"
              initial="initial"
              animate="in"
              exit="out"
              variants={fadeVariants}
              className="page-container"
            >
              <NotFound lang={lang} />
            </motion.div>
          }
        />

        {/* Catch-all for ALL unknown routes - MUST BE LAST */}
        <Route
          path="*"
          element={
            <motion.div
              key="catch-all"
              initial="initial"
              animate="in"
              exit="out"
              variants={fadeVariants}
              className="page-container"
            >
              <NotFound lang={lang} />
            </motion.div>
          }
        />
      </Routes>
    </AnimatePresence>
    </Suspense>
  );
};

export default AnimatedRoutes;
